import { useMemo, useState } from "react";
import { format, getUnixTime } from "date-fns";
import { InputBase, styled, Box } from "@mui/material";

import { DataGrid } from "components/DataGrid";
import { useGetPendingTransactionsQuery } from "api/transactions";
import { SearchIcon } from "components/Icons";
import { formatToNaira } from "utils/formatCurrency";
import { DateRangePicker } from "components/DateRangePicker";

import { UpdateTransactionStatus } from "./components/UpdateTransactionStatus";
import { TransactionMenu } from "./components/TransactionMenu";

const Search = styled("div")(({ theme }) => ({
  position: "relative",
}));

const SearchIconWrapper = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: "100%",
  position: "absolute",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  border: "1.5px solid #EFF1F4",
  borderRadius: "8px",
  "& .MuiInputBase-input": {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create("width"),
    width: "100%",
  },
}));

const columns = ({ handleUpdateTransactionStatus }) => [
  { field: "accountName", headerName: "Account NAME", flex: 1 },
  {
    field: "accountNo",
    headerName: "Account Number",
    flex: 1,
  },
  {
    field: "amount",
    headerName: "TOTAL AMOUNT",
    flex: 1,
    renderCell: ({ row }) => formatToNaira(row.amount),
  },
  { field: "bankName", headerName: "Bank Name", flex: 1 },
  {
    field: "paymentMode",
    headerName: "Payment Mode",
    flex: 1,
  },
  {
    field: "createdOn",
    headerName: "DATE & TIME",
    flex: 1,
    valueFormatter: ({ value }) => {
      if (!value) return "—";
      return format(new Date(value * 1000), "dd/MM/yyyy hh:mm a");
    },
  },
  {
    field: "",
    headerName: "",
    align: "center",
    renderCell: (params) => (
      <TransactionMenu
        handleUpdateTransactionStatus={handleUpdateTransactionStatus}
        transaction={params.row}
      />
    ),
  },
];
const PAGE_SIZE = 10;
const SORT_ORDER = "date-desc";

export const Transactions = () => {
  const [search, setSearch] = useState("");
  const [from, setFrom] = useState();
  const [to, setTo] = useState();
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  const handleUpdateTransactionStatus = (transaction) => {
    setSelectedTransaction(transaction);
  };

  const params = useMemo(
    () => ({
      search,
      from,
      to,
      limit: PAGE_SIZE,
      sort: SORT_ORDER,
      restricted: 1,
    }),
    [search, from, to]
  );

  const { data, isLoading } = useGetPendingTransactionsQuery(params);

  const handleSearch = (event) => {
    setSearch(event.target.value);
  };

  const handleSelectRange = (range) => {
    setFrom(getUnixTime(range.startDate));
    setTo(getUnixTime(range.endDate));
  };

  return (
    <Box paddingTop={3}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={3}
      >
        <Search>
          <SearchIconWrapper>
            <SearchIcon />
          </SearchIconWrapper>
          <StyledInputBase
            placeholder="Search"
            inputProps={{ "aria-label": "search" }}
            value={search}
            onChange={handleSearch}
          />
        </Search>
        <Box display="flex" alignItems="center" gap={0.5}>
          <DateRangePicker handleSelectRange={handleSelectRange} />
        </Box>
      </Box>
      <DataGrid
        rows={data || []}
        getRowId={(row) => row.id}
        columns={columns({ handleUpdateTransactionStatus })}
        loading={isLoading}
        disableColumnMenu
        disableRowSelectionOnClick
        pagination
        pageSize={PAGE_SIZE}
      />
      <UpdateTransactionStatus
        open={Boolean(selectedTransaction)}
        transaction={selectedTransaction}
        handleClose={() => setSelectedTransaction(null)}
        meta={{ params }}
      />
    </Box>
  );
};
